// src/Studio/timeline/clipIntervalCache.ts
// Pre-sorted interval lookup for O(log n + k) active-clip queries.
// Replaces O(n) per-frame iteration in syncVideosAtPlayhead / syncElementVisibilityAtPlayhead.

import type { Clip } from './EditorStore';

export class ClipIntervalCache {
  private sorted: Clip[] = [];
  private version = 0;
  private lastClipsRef: Clip[] | null = null;

  /**
   * Rebuild the sorted index. Only rebuilds if the clips array reference changed.
   * Call this before querying — it's a no-op if clips haven't changed.
   */
  rebuild(clips: Clip[]): void {
    if (clips === this.lastClipsRef) return; // Same reference, skip
    this.lastClipsRef = clips;
    this.sorted = [...clips].sort((a, b) => a.start - b.start);
    this.version++;
  }

  /**
   * Find all clips active at the given time.
   * A clip is active when clip.start <= time < clip.end.
   * Returns clips in start-time order.
   */
  getActiveClips(time: number): Clip[] {
    const result: Clip[] = [];
    const arr = this.sorted;
    const len = arr.length;

    // Binary search: find the rightmost clip where start <= time
    let lo = 0;
    let hi = len - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      if (arr[mid].start <= time) {
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    // hi is now the index of the last clip with start <= time

    // Walk backwards from hi to find all clips that contain `time`
    for (let i = hi; i >= 0; i--) {
      const c = arr[i];
      if (c.end <= time) {
        // Since sorted by start, if this clip ends before time,
        // earlier clips with even earlier starts might still overlap if they have long durations.
        // So we must continue checking — but we can break if start + maxPossibleDuration < time.
        // For simplicity and correctness, just check all remaining clips.
        // In practice, clips rarely overlap much so this loop is very short.
        continue;
      }
      result.push(c);
    }

    return result;
  }

  /**
   * Find all clips overlapping the time range [lo, hi].
   * A clip overlaps when clip.start <= hi AND clip.end > lo.
   * Uses binary search to skip clips starting after hi.
   */
  getClipsInRange(lo: number, hi: number): Clip[] {
    const result: Clip[] = [];
    const arr = this.sorted;
    const len = arr.length;

    // Binary search for upper bound: first index where start > hi
    let left = 0, right = len;
    while (left < right) {
      const mid = (left + right) >>> 1;
      if (arr[mid].start <= hi) left = mid + 1;
      else right = mid;
    }
    // Only check indices 0..left-1 (clips starting at or before hi)
    for (let i = 0; i < left; i++) {
      if (arr[i].end > lo) result.push(arr[i]);
    }

    return result;
  }

  get currentVersion(): number {
    return this.version;
  }
}
